import { useState, useEffect } from 'react';

/**
 * `Date.now()`, re-read once a second, so running clocks advance smoothly
 * between data fetches.
 *
 * One interval per consumer, not one per clock: a page showing a dozen
 * call timers calls this once at the top and passes `now` down. Twelve
 * separate intervals would tick out of step with each other, which reads
 * as jitter on a wall of timers that should all move together.
 *
 * Pass `enabled: false` to stop the interval when nothing is on screen to
 * count — an empty list has no clocks, and a 1Hz re-render of nothing is
 * pure battery.
 */
export function useSecondTicker(enabled = true): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;

    // Re-read immediately: when the ticker is re-enabled after a pause,
    // the state still holds the timestamp from when it stopped, and
    // waiting a second to correct it shows a visibly stale clock.
    setNow(Date.now());

    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled]);

  return now;
}
